/**
 * The meeting routes `RelayStore` does not carry.
 *
 * The store is the seam for everything a screen reads and re-reads — the
 * roster, the lobby, the meeting list. These are the one-shot writes and reads
 * that have no cached form: a policy edit, a host op, the transcript of a
 * meeting that is over. Putting them behind the store would mean caching
 * answers nothing asks for twice.
 *
 * Shapes are `relay-server/docs/calls-and-meetings.md`, Part 2. Where this file
 * and that document disagree, this file is wrong.
 *
 * ── The guest's credential is a header ──────────────────────────────────────
 * A guest has no session cookie; `POST /meetings/join` hands back a
 * single-meeting token which every later request presents as
 * `x-relay-meeting-token`. It is scoped to one meeting with a twelve-hour
 * ceiling, and it never goes in a URL — a credential in a query string is a
 * credential in an access log.
 *
 * `joinByToken` hands it to `lib/api.ts`, which is what puts it on the requests
 * this file does not make: the SFU signalling, the roster poll and the
 * recording upload all go through `api` directly. The explicit `asGuest` below
 * stays because these routes take the token as an argument and reading it from
 * the argument is clearer than reading it from module state.
 */

import { api, GUEST_HEADER, setMeetingToken } from '../../lib/api';
import type { Admission, MeetingPolicy, MeetingRole } from '../../lib/relay/types';

const asGuest = (token?: string): RequestInit =>
  token ? { headers: { [GUEST_HEADER]: token } } : {};

/** An id in a path, encoded — as `lib/relay/api.ts` and `lib/calls/sfu.ts`
 *  already do it. These ids are the server's own and carry nothing that needs
 *  escaping today, which is exactly why the one that eventually does would
 *  arrive as a 404 nobody could explain. */
const enc = encodeURIComponent;

// ── the door ────────────────────────────────────────────────────────────────

/** What `POST /api/relay/meetings/join` answers. `meetingToken` is present
 *  only for a guest — a signed-in visitor keeps their own session. */
export interface JoinAnswer {
  meetingId: string;
  title: string;
  status: string;
  guest: boolean;
  participantId: string;
  displayName: string;
  state: Admission;
  meetingToken?: string;
  expiresAt?: number;
}

/**
 * Redeem the link.
 *
 * `displayName` is omitted for somebody who is signed in: the server mints no
 * guest session for an account, and a name typed into a box would be thrown
 * away anyway.
 *
 * The answer's `meetingToken` is put where every later request will find it.
 * Without that step a guest is admitted to a meeting whose media they cannot
 * reach: `POST …/session` is `api` like everything else, and `api` is the only
 * thing that knows the header.
 */
export async function joinByToken(token: string, displayName?: string): Promise<JoinAnswer> {
  const answer = await api<JoinAnswer>('/api/relay/meetings/join', {
    method: 'POST',
    body: JSON.stringify(displayName === undefined ? { token } : { token, displayName })
  });
  if (answer.meetingToken) setMeetingToken(answer.meetingToken);
  return answer;
}

export interface Standing {
  meetingId: string;
  title: string;
  status: string;
  participantId: string;
  displayName: string;
  guest: boolean;
  state: Admission;
}

/** Where a party stands. The poll a knocking guest runs; it reports a standing
 *  and never creates one. */
export function myStanding(meetingId: string, guestToken?: string): Promise<Standing> {
  return api<Standing>(`/api/relay/meetings/${enc(meetingId)}/me`, asGuest(guestToken));
}

// ── host ops ────────────────────────────────────────────────────────────────

export function muteParty(meetingId: string, partyId: string): Promise<unknown> {
  return api(`/api/relay/meetings/${enc(meetingId)}/parties/${enc(partyId)}/mute`, { method: 'POST' });
}

export function unmuteParty(meetingId: string, partyId: string): Promise<unknown> {
  return api(`/api/relay/meetings/${enc(meetingId)}/parties/${enc(partyId)}/mute`, { method: 'DELETE' });
}

export function stopShare(meetingId: string, partyId: string): Promise<unknown> {
  return api(`/api/relay/meetings/${enc(meetingId)}/parties/${enc(partyId)}/stop-share`, { method: 'POST' });
}

/** The whole policy object, validated server-side. A partial one is not a
 *  thing the route accepts. */
export function setPolicy(meetingId: string, policy: MeetingPolicy): Promise<unknown> {
  return api(`/api/relay/meetings/${enc(meetingId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ policy })
  });
}

// Starting and stopping a recording is `CallSession.recording`, not a call from
// here: the flag and this browser's own uploading are one action, and the
// interface already says so.

// ── after the meeting ───────────────────────────────────────────────────────

export interface TranscriptSegment {
  participantId: string;
  displayName: string;
  startMs: number;
  endMs: number;
  text: string;
}

export interface Mark {
  id: string;
  participantId: string;
  displayName: string;
  atMs: number;
  label: string;
}

export interface Chunk {
  id: string;
  seq: number;
  offsetMs: number;
  durationMs: number;
  bytes: number;
  contentType: string;
  transcribed: boolean;
}

export interface RecordingAnswer {
  since: number;
  participants: { participantId: string; displayName: string; chunks: Chunk[] }[];
}

export function transcript(
  meetingId: string,
  guestToken?: string
): Promise<{ segments: TranscriptSegment[]; marks: Mark[] }> {
  return api(`/api/relay/meetings/${enc(meetingId)}/transcript`, asGuest(guestToken));
}

export function recording(meetingId: string, guestToken?: string): Promise<RecordingAnswer> {
  return api<RecordingAnswer>(`/api/relay/meetings/${enc(meetingId)}/recording`, asGuest(guestToken));
}

/** Where the bytes of one chunk are. An `<audio src>`, not a fetch. */
export function chunkUrl(meetingId: string, chunkId: string): string {
  return `/api/relay/meetings/${enc(meetingId)}/recording/chunks/${enc(chunkId)}`;
}

// ── boards ──────────────────────────────────────────────────────────────────

interface BoardRow {
  id: string;
  meetingId: string;
}

/**
 * The meeting's board, made on the first ask.
 *
 * One board per meeting: everybody pressing the button lands on the same
 * canvas, which is the whole point of a board in a meeting. The list is asked
 * first because a second press by a second person must not make a second board.
 */
export async function openMeetingBoard(meetingId: string, guestToken?: string): Promise<string> {
  const { boards } = await api<{ boards: BoardRow[] }>(
    `/api/relay/boards?meeting=${encodeURIComponent(meetingId)}`,
    asGuest(guestToken)
  );
  const existing = boards.find((b) => b.meetingId === meetingId);
  if (existing) return existing.id;

  const made = await api<BoardRow>('/api/relay/boards', {
    method: 'POST',
    body: JSON.stringify({ meetingId }),
    ...asGuest(guestToken)
  });
  return made.id;
}

/** Host and cohost both satisfy `'host'`, which is the one role question the
 *  room asks over and over. */
export const isHost = (role: MeetingRole): boolean => role === 'host' || role === 'cohost';
